import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * Catalog history: every change to the food and exercise catalog, who made it
 * and why, the batches developers push, and Revert.
 *
 * Production is the catalog's master copy. A developer pulls it, researches
 * and fixes facts locally, and pushes the result as a **batch**:
 * `{ "<domain>": { "<slug or key>": <entry> } }`, the entry shape the curation
 * files have always used, each entry carrying its provenance (`source`,
 * `model`, `updatedAt`, `note`, `review`). `POST /admin/catalog/batches`
 * first answers with a dry run (what each entry would change, field by field,
 * and what it refuses), then applies it in one transaction.
 *
 * - **Conflicts.** The batch states `base`, when its sender last pulled. A
 *   field production changed after that is a conflict: the entry is not
 *   written and production's newer value stands.
 * - **History.** Every row the catalog writes, whoever writes it, is a
 *   change: the row before and after, the columns that moved, who, the
 *   source (`console`, `push`, `curation`, `revert:<id>`, `system`), the note
 *   and the sources.
 * - **Revert.** Puts the earlier values back as a new change, through the
 *   same rules a forward change meets. Nutrition and a serving policy revert
 *   as one group each. A field production changed since is never overwritten.
 */
/** Every kind of entry a batch can carry, in the order the API applies them. */
export const CATALOG_DOMAINS = [
    'dishes',
    'ingredients',
    'jain-exclusions',
    'servability',
    'portions',
    'cuisines',
    'common',
    'exercises',
    'exercise-attributes',
    'exercise-edges',
    'meal-roles',
    'display-names',
    'feasibility',
    'health-tiers',
    'serving-policies',
    'diet-classes',
    'recipes',
    'nutrition',
    'meal-patterns',
    'seasonality',
    'meal-times',
    'exercise-animations',
];
/** The tables History records. */
export const CATALOG_TABLES = [
    'foods',
    'food_portions',
    'food_aliases',
    'food_recipes',
    'food_ingredients',
    'exercises',
    'exercise_edges',
    'exercise_aliases',
    'meal_patterns',
];
export const CATALOG_CHANGE_OPS = ['insert', 'update', 'delete'];
/** Mirrors `CatalogBatchStatus`. */
export const CATALOG_BATCH_STATUSES = ['applied', 'reverted'];
/**
 * What made a change, as the console names it: an operator's edit, an
 * approved food or exercise request, a developer's pushed batch (or a curation
 * run), a revert, or a write nobody made (a migration or a backfill).
 */
export const CATALOG_EVENT_KINDS = ['console', 'request', 'batch', 'revert', 'system'];
/** The kind a change's `source` belongs to. */
export function catalogEventKindOf(source) {
    if (source === 'console')
        return 'console';
    if (/^[a-z_]+_request:/.test(source))
        return 'request';
    if (source.startsWith('revert:'))
        return 'revert';
    if (source === 'push' || source === 'curation' || source.startsWith('local:'))
        return 'batch';
    return 'system';
}
/** What History is about: a food (with its portions, names and recipe), an exercise, a meal pattern. */
export const CATALOG_SUBJECTS = ['food', 'exercise', 'pattern'];
/** The tables each subject's History reads. */
export const CATALOG_SUBJECT_TABLES = {
    food: ['foods', 'food_portions', 'food_aliases', 'food_recipes', 'food_ingredients'],
    exercise: ['exercises', 'exercise_edges', 'exercise_aliases'],
    pattern: ['meal_patterns'],
};
/**
 * What became of one entry. `change` is a dry run's "would change" and an
 * apply's "changed"; `unknown` names a slug the catalog has no row for;
 * `skipped_flagged` and `skipped_unreviewed` are entries whose review says
 * nobody has signed them off.
 */
export const CATALOG_ENTRY_STATUSES = [
    'change',
    'unchanged',
    'refused',
    'conflict',
    'unknown',
    'skipped_flagged',
    'skipped_unreviewed',
];
/** A pushed batch can be a few megabytes: a whole research run. */
export const CATALOG_BATCH_MAX_BYTES = 25 * 1024 * 1024;
const actorSchema = z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
// --- push ---------------------------------------------------------------------
export const adminCatalogBatchQuerySchema = z.object({
    /** Defaults to a dry run: nothing is written unless the caller says so. */
    dryRun: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(true)
        .transform((value) => value === true || value === 'true'),
});
export const adminCatalogBatchRequestSchema = z.object({
    /** Why: shown on the batch, and on every change an entry without its own note makes. */
    note: z.string().trim().min(1).max(2000),
    /** When the sender last pulled production. */
    base: z.string().datetime({ offset: true }),
    batch: z.record(z.string(), z.record(z.string(), z.unknown())),
    /** Where the facts came from: URLs or citations. */
    sources: z.array(z.string().trim().min(1).max(500)).max(50).optional(),
});
/** A production change made after `base` that an entry would have overwritten. */
const productionChangeSchema = z.object({
    changeId: z.string(),
    changedAt: z.string(),
    source: z.string(),
    actorUserId: z.string().nullable(),
});
const fieldOutcomeSchema = z.object({
    /** The column (camelCase, as the API names it) or a collection: portions, aliases, recipe, pattern. */
    field: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    conflict: productionChangeSchema.nullable(),
});
export const adminCatalogEntryOutcomeSchema = z.object({
    domain: z.string(),
    key: z.string(),
    status: z.enum(CATALOG_ENTRY_STATUSES),
    /** Why it was refused, or the slug the catalog lacks. */
    reasons: z.array(z.string()),
    fields: z.array(fieldOutcomeSchema),
});
export const catalogEntryCountsSchema = z.object({
    change: z.number().int(),
    unchanged: z.number().int(),
    refused: z.number().int(),
    conflict: z.number().int(),
    unknown: z.number().int(),
    skipped_flagged: z.number().int(),
    skipped_unreviewed: z.number().int(),
});
export const adminCatalogBatchResultSchema = z.object({
    /** The applied batch's id; null for a dry run. */
    batchId: z.string().uuid().nullable(),
    dryRun: z.boolean(),
    base: z.string(),
    summary: catalogEntryCountsSchema,
    entries: z.array(adminCatalogEntryOutcomeSchema),
});
// --- history --------------------------------------------------------------------
export const adminCatalogChangeSchema = z.object({
    /** A bigint, as a string. */
    id: z.string(),
    tableName: z.enum(CATALOG_TABLES),
    rowId: z.string().uuid(),
    /** The food, exercise or pattern the row belongs to. */
    slug: z.string().nullable(),
    op: z.enum(CATALOG_CHANGE_OPS),
    /** The row before and after, snake_case columns as the database names them. */
    oldRecord: z.record(z.unknown()).nullable(),
    record: z.record(z.unknown()).nullable(),
    changedFields: z.array(z.string()),
    actor: actorSchema.nullable(),
    source: z.string(),
    batchId: z.string().uuid().nullable(),
    note: z.string().nullable(),
    sources: z.array(z.string()).nullable(),
    createdAt: z.string(),
});
export const adminCatalogChangesQuerySchema = cursorPageQuerySchema.extend({
    table: z.enum(CATALOG_TABLES).optional(),
    /** Only the tables of one subject, so a food's History is not a same-slug exercise's too. */
    subject: z.enum(CATALOG_SUBJECTS).optional(),
    rowId: z.string().uuid().optional(),
    slug: z.string().trim().min(1).max(96).optional(),
    batchId: z.string().uuid().optional(),
});
export const adminCatalogBatchSchema = z.object({
    id: z.string().uuid(),
    actor: actorSchema.nullable(),
    note: z.string().nullable(),
    base: z.string(),
    status: z.enum(CATALOG_BATCH_STATUSES),
    counts: catalogEntryCountsSchema,
    /** Entries that were not applied (refused, in conflict, skipped, unknown) and why. */
    unapplied: z.array(z.object({
        domain: z.string(),
        key: z.string(),
        status: z.enum(CATALOG_ENTRY_STATUSES),
        reasons: z.array(z.string()),
        /** What the food or exercise the key names is called today; set on a batch's detail. */
        name: z.string().nullable().optional(),
        /**
         * What the entry would have written, field by field, where the engine
         * got that far: a conflict's production value, its batch value and the
         * newer change that won. Absent on batches applied before this was kept.
         */
        fields: z
            .array(fieldOutcomeSchema.extend({ conflictActor: actorSchema.nullable().optional() }))
            .optional(),
    })),
    changeCount: z.number().int(),
    createdAt: z.string(),
    revertedAt: z.string().nullable(),
});
export const adminCatalogBatchDetailSchema = adminCatalogBatchSchema.extend({
    /** The first 500 row changes, oldest first; page the rest through GET /admin/catalog/changes?batchId=. */
    changes: z.array(adminCatalogChangeSchema),
});
export const adminCatalogBatchesQuerySchema = cursorPageQuerySchema;
export const adminCatalogBatchParamsSchema = z.object({ id: z.string().uuid() });
export const adminCatalogChangeParamsSchema = z.object({
    id: z.string().regex(/^[1-9]\d{0,18}$/, 'a change id is a positive whole number'),
});
// --- revert -----------------------------------------------------------------------
export const adminCatalogRevertRequestSchema = z
    .object({
    note: z.string().trim().min(1).max(2000).optional(),
    /**
     * Only these columns of an update (snake_case, as `changedFields` names
     * them). A column of the nutrition group (energy, protein, carbohydrate,
     * fat, fibre) or the serving policy brings its whole group.
     */
    fields: z.array(z.string().trim().min(1).max(64)).min(1).max(60).optional(),
})
    // A revert needs no body at all.
    .nullish();
const revertSkipSchema = z.object({
    table: z.string(),
    rowId: z.string(),
    slug: z.string().nullable(),
    reason: z.string(),
    fields: z.array(z.object({ field: z.string(), current: z.unknown(), expected: z.unknown() })),
});
export const adminCatalogRevertResultSchema = z.object({
    /** Groups the changes the revert wrote; each has source `revert:<id>`. */
    revertBatchId: z.string().uuid(),
    /** Rows put back. */
    reverted: z.number().int(),
    /** Rows left alone because production changed them since: newer wins. */
    skipped: z.array(revertSkipSchema),
    /** Foods, exercises or patterns left alone because the result would break a rule. */
    refused: z.array(z.object({ slug: z.string().nullable(), problems: z.array(z.string()) })),
    changes: z.array(adminCatalogChangeSchema),
});
// --- events -----------------------------------------------------------------------
/**
 * One change event: the row changes one action wrote together (one console
 * save, one approved request, one pushed batch, one revert), grouped by the
 * batch id the writer stated. A write that stated none is its own event.
 */
export const adminCatalogEventSchema = z.object({
    /** The batch id, or `change:<id>` for a change no batch groups. */
    key: z.string(),
    batchId: z.string().uuid().nullable(),
    kind: z.enum(CATALOG_EVENT_KINDS),
    source: z.string(),
    actor: actorSchema.nullable(),
    /** The first change's note; a pushed batch's own note when it has one. */
    note: z.string().nullable(),
    sources: z.array(z.string()),
    startedAt: z.string(),
    endedAt: z.string(),
    /** Row changes. */
    changeCount: z.number().int(),
    /** Fields changed: every column an update moved, and one per row added or removed. */
    valueCount: z.number().int(),
    foodCount: z.number().int(),
    exerciseCount: z.number().int(),
    /** Up to three of what it touched, with a name to show. */
    subjects: z.array(z.object({ slug: z.string(), name: z.string().nullable(), subject: z.enum(CATALOG_SUBJECTS) })),
    /** The first few changes, oldest first: enough to describe a small edit in one line. */
    sample: z.array(adminCatalogChangeSchema),
    /** Set when the event is a pushed batch. */
    batch: adminCatalogBatchSchema.nullable(),
    /** For a revert: what it put back. */
    revertOf: z
        .object({
        /** The change reverted, when one change was. */
        changeId: z.string().nullable(),
        /** The batch the reverted changes belonged to. */
        batchId: z.string().uuid().nullable(),
        /** That batch's note, when it was a pushed batch. */
        batchNote: z.string().nullable(),
        /** The food request that batch approved, when it was one: the console links to it. */
        foodRequestId: z.string().uuid().nullable(),
    })
        .nullable(),
});
export const adminCatalogEventsQuerySchema = cursorPageQuerySchema.extend({
    kind: z.enum(CATALOG_EVENT_KINDS).optional(),
    /** An operator's id, or `system` for changes nobody made. */
    actor: z.union([z.string().uuid(), z.literal('system')]).optional(),
    /** Only events from this moment on. */
    since: z.string().datetime({ offset: true }).optional(),
    /** A food or exercise: matched against slugs and names. */
    search: z.string().trim().min(1).max(96).optional(),
    /** Only events that changed this food, exercise or pattern (with `subject`, only its tables). */
    slug: z.string().trim().min(1).max(96).optional(),
    subject: z.enum(CATALOG_SUBJECTS).optional(),
    /** One event by its batch id: the one a link opens. */
    batchId: z.string().uuid().optional(),
});
export const adminCatalogSummaryQuerySchema = z.object({
    since: z.string().datetime({ offset: true }).optional(),
});
/** The headline over a window: what changed, what was pushed, put back and refused. */
export const adminCatalogSummarySchema = z.object({
    since: z.string().nullable(),
    valuesChanged: z.number().int(),
    batchesPushed: z.number().int(),
    batchesReverted: z.number().int(),
    /** Entries pushed batches had refused. */
    valuesRefused: z.number().int(),
});
/** Everyone who has changed the catalog, for the Who filter. */
export const adminCatalogActorSchema = actorSchema;
const changeIdSchema = z.string().regex(/^[1-9]\d{0,18}$/, 'a change id is a positive whole number');
/**
 * Puts several changes back together, as one new change: an event's "Revert
 * all", or the changes of one food within a batch. All or nothing, like one
 * change: 409 when a field changed since, 422 when the result would break a
 * rule.
 */
export const adminCatalogRevertChangesRequestSchema = z.object({
    ids: z.array(changeIdSchema).min(1).max(1000),
    note: z.string().trim().min(1).max(2000).optional(),
});
//# sourceMappingURL=catalog-history.js.map